import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import api from '@/api/engine';
import type { CreatorProfile, MirrorReflection } from '@/api/types';
import { SettingsSection } from '@seitiate/settings-ui';
import { API_V1 } from '@/lib/env';
import { buildEngineHeaders } from '@/lib/auth';

interface CodexSearchResult {
    chunk_id: string;
    content_preview: string;
    source: string;
    trust_tier: string;
    relevance_score: number;
}

function formatPercent(value: number | null | undefined): string {
    if (typeof value !== 'number' || Number.isNaN(value)) return 'Not ready';
    return `${Math.round(value * 100)}%`;
}

export const MemoryLegibilityPanel: React.FC = () => {
    const [profile, setProfile] = useState<CreatorProfile | null>(null);
    const [mirror, setMirror] = useState<MirrorReflection | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const isUnavailable = (reason: unknown): boolean =>
        reason instanceof Error &&
        (reason.message.toLowerCase().includes('404') || reason.message.toLowerCase().includes('not available'));

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const [profileResult, mirrorResult] = await Promise.allSettled([
                    api.creator.getProfile(),
                    api.creator.getMirror(),
                ]);

                if (profileResult.status === 'fulfilled') {
                    setProfile(profileResult.value);
                } else if (!isUnavailable(profileResult.reason)) {
                    setError(profileResult.reason instanceof Error ? profileResult.reason.message : 'Failed to load memory signals');
                }

                if (mirrorResult.status === 'fulfilled') {
                    setMirror(mirrorResult.value);
                } else if (!isUnavailable(mirrorResult.reason)) {
                    setError(mirrorResult.reason instanceof Error ? mirrorResult.reason.message : 'Failed to load mirror reflection');
                }
            } finally {
                setLoading(false);
            }
        };

        void load();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500 font-mono text-sm animate-pulse">Loading memory legibility...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-400 font-mono text-sm">{error}</div>
            </div>
        );
    }

    const growthEdges = mirror?.growth_edges ?? profile?.growth_edges ?? [];

    return (
        <div className="space-y-4">
            <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">
                Memory :: Legibility
            </h3>

            <SettingsSection title="Current Readability">
                <div className="grid gap-3 md:grid-cols-2">
                    <StatCard label="Decisions analyzed" value={profile?.total_decisions_analyzed ?? 0} />
                    <StatCard label="Codex chunks" value={profile?.total_codex_chunks ?? 'Not ready'} />
                    <StatCard label="Growth edges" value={growthEdges.length} />
                    <StatCard
                        label="Codex contribution"
                        value={formatPercent(profile?.codex_contribution_rate)}
                    />
                </div>
                <p className="text-[10px] text-gray-500">
                    This is the clearest backend summary Sakme currently exposes for how legible your accumulated memory is.
                </p>
            </SettingsSection>

            <SettingsSection title="Mirror Reflection" badge={mirror?.profile_ready ? 'LIVE' : 'WARMING'}>
                {mirror?.reflection ? (
                    <p className="text-sm text-gray-300 leading-6">{mirror.reflection}</p>
                ) : (
                    <p className="text-[10px] text-gray-500">
                        The Mirror is not ready yet. It starts speaking once enough creative decisions have accumulated.
                    </p>
                )}
            </SettingsSection>

            <SettingsSection title="Growth Edges" defaultExpanded={growthEdges.length > 0}>
                {growthEdges.length > 0 ? (
                    <div className="space-y-2">
                        {growthEdges.slice(0, 4).map((edge) => (
                            <div key={`${edge.dimension}-${edge.detected_at}`} className="rounded border border-gray-800 bg-gray-900/40 p-3">
                                <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">{edge.dimension}</div>
                                <div className="mt-1 text-xs text-gray-300">{edge.description}</div>
                                <div className="mt-2 text-[10px] text-gray-500">{edge.suggested_challenge}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-[10px] text-gray-500">
                        Growth edges will appear here once profile computation has enough history to work with.
                    </p>
                )}
            </SettingsSection>

            <CodexSearchSection />
        </div>
    );
};

const CodexSearchSection: React.FC = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<CodexSearchResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [searched, setSearched] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const runSearch = useCallback(async (q: string) => {
        if (q.trim().length < 2) {
            setResults([]);
            setSearched(false);
            return;
        }
        setSearching(true);
        try {
            const resp = await fetch(
                `${API_V1}/codex/search?q=${encodeURIComponent(q.trim())}`,
                { headers: buildEngineHeaders() }
            );
            if (resp.ok) {
                const data = await resp.json();
                setResults(data.results ?? []);
            } else {
                setResults([]);
            }
        } catch {
            setResults([]);
        } finally {
            setSearching(false);
            setSearched(true);
        }
    }, []);

    const handleInput = (value: string) => {
        setQuery(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => void runSearch(value), 400);
    };

    return (
        <SettingsSection title="Codex Search" defaultExpanded={false}>
            <div className="relative">
                <Search
                    className="absolute left-2.5 top-2 w-3.5 h-3.5"
                    style={{ color: 'var(--theme-text-muted)' }}
                />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => handleInput(e.target.value)}
                    placeholder="Search your knowledge base…"
                    className="w-full pl-8 pr-3 py-1.5 text-xs rounded"
                    style={{
                        background: 'var(--theme-surface-secondary)',
                        border: '1px solid var(--theme-surface-tertiary)',
                        color: 'var(--theme-text-primary)',
                        fontFamily: 'var(--theme-font-mono)',
                        borderRadius: 'var(--theme-radius-sm)',
                        outline: 'none',
                    }}
                />
                {searching && (
                    <Loader2
                        className="absolute right-2.5 top-2 w-3.5 h-3.5 animate-spin"
                        style={{ color: 'var(--theme-text-muted)' }}
                    />
                )}
            </div>
            {searched && results.length === 0 && (
                <div
                    className="text-[10px] mt-2"
                    style={{ color: 'var(--theme-text-muted)', fontFamily: 'var(--theme-font-mono)' }}
                >
                    No results found.
                </div>
            )}
            {results.length > 0 && (
                <div className="mt-2 space-y-1">
                    {results.slice(0, 8).map((r) => (
                        <div
                            key={r.chunk_id}
                            className="px-3 py-2 text-[10px] rounded"
                            style={{
                                background: 'color-mix(in srgb, var(--theme-surface-tertiary) 50%, transparent)',
                                border: '1px solid var(--theme-surface-tertiary)',
                                borderRadius: 'var(--theme-radius-sm)',
                            }}
                        >
                            <div
                                className="line-clamp-2"
                                style={{ color: 'var(--theme-text-primary)', fontFamily: 'var(--theme-font-mono)' }}
                            >
                                {r.content_preview}
                            </div>
                            <div
                                className="mt-1 flex items-center gap-2"
                                style={{ color: 'var(--theme-text-muted)', fontFamily: 'var(--theme-font-mono)' }}
                            >
                                <span>{r.source}</span>
                                <span>·</span>
                                <span>{r.trust_tier}</span>
                                <span>·</span>
                                <span>{(r.relevance_score * 100).toFixed(0)}% match</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </SettingsSection>
    );
};

const StatCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
    <div className="rounded border border-gray-800 bg-gray-900/40 p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">{label}</div>
        <div className="mt-2 text-lg font-semibold text-gray-200">{value}</div>
    </div>
);
